/**
 * @file Renders the investor cell of the transfer table, with the controls that link a source investment to a target one.
 */
import { useOutletContext } from "react-router-dom";
import { Tooltip, Space, Button, Dropdown } from "antd";
import { EnterOutlined, WarningOutlined, CloseOutlined } from "@ant-design/icons";

import { JOINT, INDIVIDUAL } from "@api/services/investments/shapes/ProfileTypeEnum";

import InvestorColumn from "../InvestorColumn";
import computeInvestmentName from "../helpers/computeInvestmentName";
import type { SetState, Investment, CustomTarget, OutletContext } from "./types";

const LABEL_LINK = "Link Profile";

const TITLE_SAME_TYPE = "The target project has investment profiles of the same type — ensure they are distinct.";
const TITLE_INDIVIDUAL = "The target project has individual profiles — ensure they are distinct.";
const TITLE_JOINT = "The target project has joint profiles — ensure they are distinct.";

const ENTER_ICON_STYLE = { transform: "scaleX(-1) translateY(-3px)" };
const WARNING_ICON_STYLE = { color: "red" };

type Props = {
  isIncluded: boolean;
  investment: Investment;
  customTargets: CustomTarget[];
  sourceProjectId: string;
  setCustomTargets: SetState<CustomTarget[]>;
  targetInvestments: Investment[];
};

/**
 * Renders the investor name for a transfer row, with the controls to link or unlink its target investment.
 */
const TableInvestmentColumn = ({
  isIncluded,
  investment,
  customTargets,
  sourceProjectId,
  setCustomTargets,
  targetInvestments,
}: Props) => {
  const { computeAccountFullname } = useOutletContext<OutletContext>();

  const name = computeInvestmentName(computeAccountFullname, investment);

  const isSourceInvestment = investment.projectId === sourceProjectId;
  const isTargetInvestment = !isSourceInvestment;

  if (isTargetInvestment) {
    const isCustomTargetInvestment = !!customTargets
      .find(({ targetInvestmentId }) => investment.id === targetInvestmentId);

    /**
     * Removes the custom link between the source investment and its target investment.
     */
    const unlinkCustomInvestment = () =>
      setCustomTargets((prev) =>
        [ ...prev.filter(({ targetInvestmentId }) => investment.id !== targetInvestmentId) ],
      );

    return (
      <Space>
        <EnterOutlined style={ENTER_ICON_STYLE} />

        <InvestorColumn
          name={<>{name}</>}
          isBold={isIncluded}
          investment={investment}
        />

        {
          isCustomTargetInvestment && (
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={unlinkCustomInvestment}
            />
          )
        }
      </Space>
    );
  }

  const { investorAccountId, profileType } = investment;

  /**
   * Lists the target investments for the same investor account that match the given profile type.
   */
  const listTargetInvestmentsForType = (targetProfileType: string) =>
    targetInvestments
      .filter((targetInvestment) => targetInvestment.investorAccountId === investorAccountId)
      .filter((targetInvestment) => targetInvestment.profileType === targetProfileType);

  const isIndividual = profileType === INDIVIDUAL;
  const isJoint = profileType === JOINT;
  const isPersonal = isIndividual || isJoint;

  /**
   * Lists the target investments that could be linked to the source investment's investor account.
   */
  const getRelatedTargetInvestments = () => {
    if (isPersonal) {
      return [
        ...listTargetInvestmentsForType(INDIVIDUAL),
        ...listTargetInvestmentsForType(JOINT),
      ];
    }

    return listTargetInvestmentsForType(profileType);
  };

  const relatedTargetInvestments = getRelatedTargetInvestments();

  const hasRelatedInvestments =
    isIncluded &&
    relatedTargetInvestments.length > 0;

  /**
   * Returns the warning tooltip title for the linking dropdown, based on the investment's profile type.
   */
  const getTooltipTitle = () => {
    if (!isPersonal) {
      return TITLE_SAME_TYPE;
    }

    if (isIndividual) {
      return TITLE_INDIVIDUAL;
    }

    return TITLE_JOINT;
  };

  /**
   * Records a custom link from the source investment to the given target investment.
   */
  const linkTargetInvestment = (targetInvestmentId: string) => {
    const sourceInvestmentId = investment.id;
    setCustomTargets((prev) => [ ...prev, { sourceInvestmentId, targetInvestmentId } ]);
  };

  return (
    <Space>
      <InvestorColumn
        name={<>{name}</>}
        isBold={isIncluded}
        investment={investment}
      />

      {
        hasRelatedInvestments && (
          <Dropdown
            menu={{
              items: relatedTargetInvestments
                .map(({ id: key, name: label }) => ({
                  /**
                   * Links the source investment to this menu item's target investment.
                   */
                  onClick: () => linkTargetInvestment(key),
                  key,
                  label,
                })),
            }}
            arrow={true}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
            >
              <Tooltip
                title={getTooltipTitle()}
                placement="top"
              >
                <WarningOutlined style={WARNING_ICON_STYLE} />
              </Tooltip>

              {LABEL_LINK}
            </Button>
          </Dropdown>
        )
      }
    </Space>
  );
};

export default TableInvestmentColumn;
